import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Kbd,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, type RowSelectionState, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';

import { AggregatedBondTrade, BondAllocation } from '../../../_openapi/portfolio-optimizer';
import {
    useGenerateSchwabAggregatedBondTrades,
    useGenerateSchwabBondAllocationFile,
    useSaveSchwabExecutedBondTrades,
    useSchwabAggregatedBondTrades,
} from '../../../api/trade';
import { expanderCol, numberCol, selectCol, textCol } from '../../../widgets/Table/columnHelpers/colDef';
import { Table } from '../../../widgets/Table/Table';
import { TradeSideTag } from '../../../widgets/TradeSideTag';

const columnHelper = createColumnHelper<AggregatedBondTrade>();
const columns = [
    columnHelper.display(expanderCol()),
    columnHelper.display(selectCol()),
    columnHelper.accessor('optimizationId', textCol({ id: 'optimizationId' })),
    columnHelper.accessor('masterAccount', textCol({ id: 'masterAccount' })),
    columnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    columnHelper.accessor('side', {
        ...textCol({ id: 'side' }),
        cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
    }),
    columnHelper.accessor('aggregatedQuantity', numberCol({ id: 'aggregatedQuantity' })),
];

const EMPTY_DATA: AggregatedBondTrade[] = []; // useReactTable needs a stable reference in order to prevent infinite re-renders

export const AggregatedBondTradesDrawer = ({
    optimizationId,
    excludedOrderIds,
    isOpen,
    onClose,
}: {
    optimizationId: number;
    excludedOrderIds: string[];
    isOpen: boolean;
    onClose: () => void;
}) => {
    const { data = EMPTY_DATA } = useSchwabAggregatedBondTrades({ optimizationId });

    const { mutate: generateSchwabAggregatedBondTrades, isPending: isGeneratePending } =
        useGenerateSchwabAggregatedBondTrades();
    const { mutate: generateSchwabBondAllocationFile, isPending: isDownloadPending } =
        useGenerateSchwabBondAllocationFile();
    const { mutate: saveSchwabExecutedBondTrades, isPending: isSavePending } = useSaveSchwabExecutedBondTrades();

    const [rowSelection, setRowSelection] = useState<RowSelectionState>(
        data.reduce<RowSelectionState>((result, trade) => {
            result[getAggregatedBondTradeId(trade)] = trade.executed;
            return result;
        }, {}),
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            rowSelection,
        },
        onRowSelectionChange: setRowSelection,
        enableColumnFilters: false,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
        getRowId: getAggregatedBondTradeId,
        getRowCanExpand: (row) => row.original.allocations.length > 0,
    });

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />

                <DrawerHeader borderBottomWidth={1}>Aggregated bond trades</DrawerHeader>

                <DrawerBody>
                    <Flex justifyContent="space-between" borderBottomWidth={1} py={3} mb={3}>
                        <Flex gap={2}>
                            <Button
                                colorScheme="teal"
                                size="sm"
                                isLoading={isGeneratePending}
                                onClick={() =>
                                    generateSchwabAggregatedBondTrades({
                                        optimizationId,
                                        requestBody: excludedOrderIds,
                                    })
                                }
                            >
                                Generate aggregated bond trades
                            </Button>
                            <Button
                                colorScheme="teal"
                                size="sm"
                                isDisabled={!data.length || isGeneratePending}
                                isLoading={isDownloadPending}
                                onClick={() => generateSchwabBondAllocationFile({ optimizationId })}
                            >
                                Download Schwab bond allocation CSV
                            </Button>
                        </Flex>
                        <Flex gap={4} alignItems="center">
                            <Text fontSize="sm" color="gray.500">
                                Block trades:{' '}
                                <Text as="span" fontWeight="semibold" color="gray.200">
                                    {data.length}
                                </Text>
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                Individual account trades:{' '}
                                <Text as="span" fontWeight="semibold" color="gray.200">
                                    {data.reduce((sum, t) => sum + (t.allocations?.length ?? 0), 0)}
                                </Text>
                            </Text>
                        </Flex>
                    </Flex>
                    <Table
                        table={table}
                        SubComponent={({ row }) => <BondAllocationTable data={row.original.allocations} />}
                    />
                    <Button
                        colorScheme="teal"
                        size="sm"
                        isDisabled={!data.length || isGeneratePending}
                        isLoading={isSavePending}
                        onClick={() =>
                            saveSchwabExecutedBondTrades({
                                optimizationId,
                                executedBondTrade: table.getSelectedRowModel().rows.map((row) => ({
                                    symbol: row.original.symbol,
                                    side: row.original.side,
                                })),
                            })
                        }
                    >
                        Save selection
                    </Button>
                </DrawerBody>

                <DrawerFooter justifyContent="space-between">
                    <Box>
                        <Alert status="info" fontSize="xs">
                            <AlertIcon boxSize={4} />
                            <Text>
                                You can close this panel by hitting <Kbd>Esc</Kbd> or clicking the backdrop
                            </Text>
                        </Alert>
                    </Box>
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export const useAggregatedBondTradesDrawerControls = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    return {
        viewAggregatedBondTrades: onOpen,
        isOpen,
        onClose,
    };
};

const getAggregatedBondTradeId = (trade: AggregatedBondTrade): string => {
    return JSON.stringify([trade.symbol, trade.side]);
};

const bondAllocationColumnHelper = createColumnHelper<BondAllocation>();
const bondAllocationColumns = [
    bondAllocationColumnHelper.accessor('optimizationId', textCol({ id: 'optimizationId' })),
    bondAllocationColumnHelper.accessor('pId', textCol({ id: 'pId' })),
    bondAllocationColumnHelper.accessor('aC', textCol({ id: 'aC' })),
    bondAllocationColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    bondAllocationColumnHelper.accessor('side', {
        ...textCol({ id: 'side' }),
        cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
    }),
    bondAllocationColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
];

const BondAllocationTable = ({ data }: { data: BondAllocation[] }) => {
    const table = useReactTable({
        data,
        columns: bondAllocationColumns,
        enableColumnFilters: false,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
    });
    return <Table table={table} />;
};
